import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAuthContext } from "@hooks/use-auth-context";
import { useTranslation } from "@i18n/hooks";

import { H1Span, P2Span, P3Span } from "@themes/font-tags";
import { getSupabaseErrorMessage } from "@util/supabase-error.utils";
import {
	formWrapperClass,
	inputsWrapperClass,
	invitationTitleWrapperClass,
	textCenterClass,
} from "./one-time-password-login.styles";
import { OneTimePasswordVerificationForm } from "./parts/one-time-password-form/one-time-password-form.component";
import {
	type OneTimePasswordVerificationFormClass,
	type OneTimePasswordVerificationFormState,
} from "./parts/one-time-password-form/one-time-password-form.types";
import { OneTimePasswordRequestForm } from "./parts/one-time-password-request-form/one-time-password-request-form.component";
import { type OneTimePasswordRequestFormClass } from "./parts/one-time-password-request-form/one-time-password-request-form.types";
import { STORAGE_KEY_WAITING_FOR_TOKEN } from "../../../consts";
import { Button, ButtonVariant } from "../../button";

export const OneTimePasswordLogin = () => {
	const { t } = useTranslation("common");
	const { setLastLoginMethod, signInWithOneTimePassword, verifyOtp } = useAuthContext();

	const [isSendingCode, setIsSendingCode] = useState(false);
	const [isVerifying, setIsVerifying] = useState(false);
	const [showVerification, setShowVerification] = useState(false);
	const [email, setEmail] = useState("");

	const requestFormRef = useRef<OneTimePasswordRequestFormClass>(null);

	const handleOnRequestSubmit = async () => {
		if (!requestFormRef.current?.values.email) return;
		const emailValue = requestFormRef.current.values.email;
		try {
			setIsSendingCode(true);
			await signInWithOneTimePassword(emailValue);
			setEmail(emailValue);
			setLastLoginMethod("email");
			setShowVerification(true);
		} catch (error: unknown) {
			try {
				const errorMessage = getSupabaseErrorMessage(error);
				toast.error(`${errorMessage.summary}: ${errorMessage.detail}`);
			} catch (_errorHandlingError) {
				toast.error(t("login.sendingError"));
			}
		} finally {
			setIsSendingCode(false);
		}
	};

	const verificationFormRef = useRef<OneTimePasswordVerificationFormClass>(null);

	const handleOnVerificationSubmit = async (values: OneTimePasswordVerificationFormState) => {
		if (!email) return;
		setIsVerifying(true);
		try {
			await verifyOtp(email, values.oneTimePassword);
			localStorage.setItem(STORAGE_KEY_WAITING_FOR_TOKEN, "true");
		} catch (error: unknown) {
			try {
				const errorMessage = getSupabaseErrorMessage(error);
				toast.error(`${errorMessage.summary}: ${errorMessage.detail}`);
			} catch (_errorHandlingError) {
				toast.error(t("login.verifyError"));
			}
			setIsVerifying(false);
		}
	};

	const handleOnRetryClick = () => {
		setShowVerification(false);
		setEmail("");
		verificationFormRef.current?.reset();
	};

	const submitRequestButtonLabel = isSendingCode
		? t("login.sendingCode")
		: t("login.requestCode");

	const submitVerificationButtonLabel = isVerifying
		? t("login.verifyingCode")
		: t("login.verifyCode");

	if (!showVerification) {
		return (
			<div className={inputsWrapperClass}>
				<OneTimePasswordRequestForm
					ref={requestFormRef}
					obSubmit={handleOnRequestSubmit}
					isSubmitting={isSendingCode}
					buttonLabel={submitRequestButtonLabel}
				/>
			</div>
		);
	}

	return (
		<div className={formWrapperClass}>
			<div className={textCenterClass}>
				<div className={invitationTitleWrapperClass}>
					<H1Span>{t("login.codeSent")}</H1Span>
					<P2Span>{t("login.codeSentDescription", { email })}</P2Span>
					<P3Span>{t("login.enterCode")}</P3Span>
				</div>
			</div>

			<OneTimePasswordVerificationForm
				ref={verificationFormRef}
				onSubmit={handleOnVerificationSubmit}
				isSubmitting={isVerifying}
				buttonLabel={submitVerificationButtonLabel}
			/>
			<div className={textCenterClass}>
				<P3Span>
					{t("login.noEmailReceived")}
					<Button
						label={t("login.tryAgain")}
						variant={ButtonVariant.Textlink}
						onClick={handleOnRetryClick}
					/>
				</P3Span>
			</div>
		</div>
	);
};
